import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import axios from "../utils/axios";
import { Button, SearchInput, Select, ServerTablePagination } from "../components/ui";
import LeadProspectAvatar from "../components/ui/LeadProspectAvatar";
import CreateListModal from "../components/Modals/CreateListModal";
import SelectListForImportModal from "../components/Modals/SelectListForImportModal";
import AddProspectsChooserModal from "../components/Modals/AddProspectsChooserModal";
import { ownerSelector } from "../store/store";
import { createList } from "../store/features/ownerActions";
import {
  fetchLeadsDatabase,
  fetchLists,
  resolveDashboardScope,
} from "../utils";

type LeadStage = "Interested" | "Connected" | "Replied" | "New";

type MemberList = {
  _id?: string;
  listName?: string;
};

type LeadRow = {
  id: string;
  name: string;
  lists: string[];
  company: string;
  contact: string;
  stage: LeadStage;
  owner: string;
  avatarUrl: string;
  avatarClass: string;
  lastActivity: string;
};

const AVATAR_CLASSES = [
  "bg-user-avatar text-user-avatar-fg",
  "bg-bg-light-blue text-dark-blue",
  "bg-bg-blue text-brand-blue",
  "bg-success-bg text-success",
  "bg-bg-grey text-muted",
];

const stageClass = (stage: LeadStage) => {
  if (stage === "Interested") return "bg-success-bg text-success";
  if (stage === "Connected") return "bg-bg-blue text-brand-blue";
  if (stage === "Replied") return "bg-proxy-warning-bg text-proxy-warning-text";
  return "bg-bg-grey text-muted";
};

const initials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "?";

const outlineBtn =
  "h-10 !border-line bg-white px-3.5 font-semibold text-dark-blue hover:bg-paper";
const primaryBtn = "h-10 px-3.5 font-semibold text-dark-blue";

const formatRelativeTime = (value?: string | Date | null) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return "Just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return "Yesterday";
  if (diffDay < 30) return `${diffDay}d ago`;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const resolveStage = (prospect: Record<string, any>): LeadStage => {
  if (Boolean(prospect?.hasReplied)) return "Replied";
  const connected = String(prospect?.connected || "").toUpperCase();
  if (connected === "ACCEPTED") return "Connected";
  if (connected === "PENDING") return "New";
  return "New";
};

const resolveContact = (prospect: Record<string, any>) => {
  const parts = ["LinkedIn"];
  const email = String(prospect?.contactInfo?.emailAddress || "").trim();
  if (email) parts.push("Email");
  const phones = prospect?.contactInfo?.phoneNumbers;
  if (Array.isArray(phones) && phones.length > 0) parts.push("Phone");
  return parts.join(" · ");
};

const resolveCompany = (prospect: Record<string, any>) => {
  const company = String(prospect?.current_company_name || "").trim();
  const title = String(prospect?.title || "").trim();
  if (company && title) return `${title} at ${company}`;
  if (company) return company;
  if (title) return title;
  const headline = String(prospect?.headline || "").trim();
  return headline || "—";
};

const resolveName = (prospect: Record<string, any>) => {
  const full = String(prospect?.full_name || "").trim();
  if (full) return full;
  const first = String(prospect?.firstName || "").trim();
  const last = String(prospect?.lastName || "").trim();
  return [first, last].filter(Boolean).join(" ") || "Unknown prospect";
};

const mapProspectToRow = (prospect: Record<string, any>, index: number): LeadRow => {
  const memberLists: MemberList[] = Array.isArray(prospect?.memberLists)
    ? prospect.memberLists
    : [];
  const listNames = memberLists
    .map((item) => String(item?.listName || "").trim())
    .filter(Boolean);
  return {
    id: String(prospect?._id || ""),
    name: resolveName(prospect),
    lists: listNames,
    company: resolveCompany(prospect),
    contact: resolveContact(prospect),
    stage: resolveStage(prospect),
    owner: String(prospect?.sender?.name || "").trim() || "—",
    avatarUrl: String(prospect?.image_url || prospect?.imageUrl || "").trim(),
    avatarClass: AVATAR_CLASSES[index % AVATAR_CLASSES.length],
    lastActivity: formatRelativeTime(
      prospect?.updatedAt || prospect?.acceptedAt || prospect?.createdAt
    ),
  };
};

const LeadsDatabase = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { owner, currentWorkspace, currentUser } = useSelector(ownerSelector);

  const workspace =
    typeof currentWorkspace === "string"
      ? (() => {
          try {
            return JSON.parse(currentWorkspace || "null");
          } catch {
            return null;
          }
        })()
      : currentWorkspace;

  const ownerId = String(owner?._id || "");
  const { resolvedUserId: userId, resolvedWorkspaceId: workspaceId } =
    resolveDashboardScope(currentUser?.userId, workspace?._id);
  const [searchParams] = useSearchParams();

  const [query, setQuery] = useState(() => searchParams.get("q") || "");
  const [debouncedQuery, setDebouncedQuery] = useState(() =>
    (searchParams.get("q") || "").trim()
  );
  const [filter, setFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [rows, setRows] = useState<LeadRow[]>([]);
  const [totalProspects, setTotalProspects] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showImportListModal, setShowImportListModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddProspectsChooser, setShowAddProspectsChooser] = useState(false);

  useEffect(() => {
    const fromUrl = searchParams.get("q") || "";
    setQuery(fromUrl);
    setDebouncedQuery(fromUrl.trim());
  }, [searchParams]);
  const [createWithSelection, setCreateWithSelection] = useState(false);
  const [assignLists, setAssignLists] = useState<
    Array<{ _id: string; listName?: string; prospectsCount?: number }>
  >([]);
  const [isAssignListsLoading, setIsAssignListsLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [newListDescription, setNewListDescription] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, filter, pageSize]);

  const loadProspects = useCallback(async () => {
    if (!workspaceId && !ownerId) {
      setRows([]);
      setTotalProspects(0);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const data = await fetchLeadsDatabase({
        ownerId,
        userId,
        workspaceId,
        page,
        limit: pageSize,
        search: debouncedQuery || undefined,
        stage: filter,
      });

      const mapped = (Array.isArray(data?.prospects) ? data.prospects : []).map(
        (prospect: any, index: number) => mapProspectToRow(prospect, index)
      );

      setRows(mapped);
      setTotalProspects(Number(data?.totalProspects || 0));
    } catch (error) {
      console.error(error);
      toast.error("Unable to load leads");
      setRows([]);
      setTotalProspects(0);
    } finally {
      setIsLoading(false);
    }
  }, [debouncedQuery, filter, ownerId, page, pageSize, userId, workspaceId]);

  useEffect(() => {
    void loadProspects();
  }, [loadProspects]);

  const visibleIds = useMemo(() => rows.map((row) => row.id), [rows]);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const toggleAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
      return;
    }
    const visible = new Set(visibleIds);
    setSelectedIds((prev) => prev.filter((id) => !visible.has(id)));
  };

  const toggleOne = (id: string, checked: boolean) => {
    setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((item) => item !== id)));
  };

  const loadAssignableLists = useCallback(async () => {
    setIsAssignListsLoading(true);
    try {
      const lists = await fetchLists(ownerId, workspaceId, userId);
      const options = (Array.isArray(lists) ? lists : [])
        .filter(
          (list: any) =>
            !list?.isDeleted && String(list?.listName || "") !== "All Prospects"
        )
        .map((list: any) => ({
          _id: String(list?._id || ""),
          listName: list?.listName || "",
          prospectsCount: Array.isArray(list?.prospects) ? list.prospects.length : 0,
        }))
        .filter((list) => list._id);
      setAssignLists(options);
      return options;
    } catch (error) {
      console.error(error);
      toast.error("Unable to load lists");
      setAssignLists([]);
      return [];
    } finally {
      setIsAssignListsLoading(false);
    }
  }, [ownerId, userId, workspaceId]);

  const openAssignModal = async () => {
    if (selectedIds.length === 0) {
      toast.info("Select one or more prospects first.");
      return;
    }
    const options = await loadAssignableLists();
    if (!options.length) {
      toast.info("Create a list first, then assign prospects to it.");
      setCreateWithSelection(true);
      setNewListName("");
      setNewListDescription("");
      setShowCreateModal(true);
      return;
    }
    setShowAssignModal(true);
  };

  const openImportListPicker = async () => {
    const options = await loadAssignableLists();
    if (!options.length) {
      toast.info("Create a list first, then import prospects into it.");
      setCreateWithSelection(false);
      setNewListName("");
      setNewListDescription("");
      setShowCreateModal(true);
      return;
    }
    setShowImportListModal(true);
  };

  const handleSelectListForImport = (listId: string) => {
    if (!listId) return;
    setShowImportListModal(false);
    navigate(`/prospects/${listId}/import`);
  };

  const openCreateModal = (withSelection: boolean) => {
    if (withSelection && selectedIds.length === 0) {
      toast.info("Select one or more prospects first.");
      return;
    }
    setCreateWithSelection(withSelection);
    setNewListName("");
    setNewListDescription("");
    setShowCreateModal(true);
  };

  const addProspectsToList = async (listId: string, prospectIds: string[]) => {
    await axios.post("/prospect/addProspectToList", {
      ownerId: ownerId || undefined,
      userId,
      workspaceId,
      listId,
      prospectIds,
    });
  };

  const handleAssignToList = async (listId: string) => {
    if (!listId || selectedIds.length === 0) return;
    setIsAssigning(true);
    try {
      await addProspectsToList(listId, selectedIds);
      const listName =
        assignLists.find((list) => list._id === listId)?.listName || "list";
      toast.success(
        `Added ${selectedIds.length} prospect${selectedIds.length === 1 ? "" : "s"} to ${listName}`
      );
      setShowAssignModal(false);
      setSelectedIds([]);
      await loadProspects();
    } catch (error) {
      console.error(error);
      toast.error("Unable to assign prospects to that list");
    } finally {
      setIsAssigning(false);
    }
  };

  const handleCreateList = async () => {
    const listName = newListName.trim();
    if (!listName) {
      toast.error("List name is required");
      return;
    }
    if (!workspaceId) {
      toast.error("Missing workspace context");
      return;
    }
    setIsCreating(true);
    try {
      const result = await dispatch(
        createList({
          ownerId: ownerId || undefined,
          userId,
          workspaceId,
          listName,
          description: newListDescription.trim(),
        }) as any
      );
      if (result?.meta?.requestStatus !== "fulfilled" || !result?.payload?._id) {
        toast.error("Unable to create list");
        return;
      }
      const createdId = String(result.payload._id);
      if (createWithSelection && selectedIds.length > 0) {
        await addProspectsToList(createdId, selectedIds);
        toast.success(
          `Created “${listName}” with ${selectedIds.length} prospect${
            selectedIds.length === 1 ? "" : "s"
          }`
        );
        setSelectedIds([]);
      } else {
        toast.success(`Created “${listName}”`);
      }
      setShowCreateModal(false);
      setNewListName("");
      setNewListDescription("");
      await loadProspects();
    } catch (error) {
      console.error(error);
      toast.error("Unable to create list");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <section className="noScroll h-full overflow-y-auto overflow-x-hidden bg-paper px-4 py-6 sm:px-9 sm:py-9">
      <div className="mx-auto max-w-[1600px]">
        <header className="mb-6">
          <h1 className="text-[27px] font-semibold tracking-tight text-dark-blue">
            Leads database
          </h1>
          <p className="mt-1 text-sm text-muted">
            One global prospect database across every List. Assign any prospect to an existing or
            new List.
          </p>
        </header>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <SearchInput
            value={query}
            onChange={setQuery}
            variant="soft"
            placeholder="Search all prospects"
            aria-label="Search all prospects"
            containerClassName="w-full max-w-[280px]"
          />
          <Select
            variant="compact"
            size="sm"
            ariaLabel="Filter prospects"
            value={filter}
            onChange={setFilter}
            options={[
              { value: "all", label: "All" },
              { value: "connected", label: "Connected" },
              { value: "replied", label: "Replied" },
              { value: "new", label: "New" },
            ]}
            className="h-10 w-[120px] !border-line font-semibold text-dark-blue"
          />
          <Button
            variant="outline"
            size="sm"
            className={outlineBtn}
            onClick={() => openCreateModal(selectedIds.length > 0)}
          >
            + Create List
          </Button>
          <Button
            variant="outline"
            size="sm"
            className={outlineBtn}
            isDisabled={isAssignListsLoading}
            onClick={() => void openAssignModal()}
          >
            Assign to List
          </Button>
          <Button
            variant="primary"
            size="sm"
            className={primaryBtn}
            onClick={() => setShowAddProspectsChooser(true)}
          >
            Add Prospects
          </Button>
          <Button
            variant="outline"
            size="sm"
            className={outlineBtn}
            onClick={() => navigate("/prospects")}
          >
            Manage Lists
          </Button>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-[9px] border border-notice-border bg-notice-bg px-[15px] py-[13px]">
          <p className="text-sm font-semibold text-dark-blue">Global database</p>
          <p className="text-xs text-notice-text">
            Every prospect from every List appears here. Select prospects to add them to an existing
            List or create a new List from the selection.
          </p>
        </div>

        <div className="overflow-hidden rounded-[10px] border border-line bg-white p-[18px]">
          {selectedIds.length > 0 ? (
            <div className="mb-3 flex flex-wrap items-center gap-3 rounded-[7px] bg-sidebar-chip px-3 py-2.5 text-xs text-white">
              <span>
                <b>{selectedIds.length} selected</b> of {totalProspects.toLocaleString()} prospects
              </span>
              <button
                type="button"
                className="text-sidebar-text hover:text-white"
                onClick={() => void openAssignModal()}
              >
                Add to existing List
              </button>
              <button
                type="button"
                className="text-sidebar-text hover:text-white"
                onClick={() => openCreateModal(true)}
              >
                + Create new List
              </button>
              <button
                type="button"
                className="text-sidebar-text hover:text-white"
                onClick={() => toast.info("Open a list to manage tags for selected prospects.")}
              >
                Add Tag
              </button>
              <button
                type="button"
                className="text-sidebar-text hover:text-white"
                onClick={() => navigate("/campaign-management/create")}
              >
                Add to Campaign
              </button>
            </div>
          ) : null}

          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] border-collapse text-left">
              <thead>
                <tr className="text-[10px] font-semibold uppercase tracking-wide text-muted">
                  <th className="w-8 pb-2.5 pr-2.5">
                    <input
                      type="checkbox"
                      aria-label="Select all visible prospects"
                      checked={allVisibleSelected}
                      onChange={(event) => toggleAll(event.target.checked)}
                      className="h-3.5 w-3.5 accent-dark-blue"
                    />
                  </th>
                  <th className="pb-2.5 pr-2.5">Prospect</th>
                  <th className="pb-2.5 pr-2.5">Lists</th>
                  <th className="pb-2.5 pr-2.5">Company</th>
                  <th className="pb-2.5 pr-2.5">Contact</th>
                  <th className="pb-2.5 pr-2.5">Stage</th>
                  <th className="pb-2.5 pr-2.5">Owner</th>
                  <th className="pb-2.5">Last activity</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 6 }).map((_, index) => (
                    <tr key={`skeleton-${index}`} className="border-t border-line">
                      <td colSpan={8} className="py-3">
                        <div className="h-4 w-full animate-pulse rounded bg-bg-grey" />
                      </td>
                    </tr>
                  ))
                ) : rows.length === 0 ? (
                  <tr className="border-t border-line">
                    <td colSpan={8} className="py-10 text-center text-sm text-muted">
                      {debouncedQuery
                        ? "No prospects match your search."
                        : filter !== "all"
                          ? `No ${filter} prospects found.`
                          : "No prospects yet. Import leads into a List to see them here."}
                    </td>
                  </tr>
                ) : (
                  rows.map((lead) => (
                    <tr key={lead.id} className="border-t border-line">
                      <td className="py-3 pr-2.5">
                        <input
                          type="checkbox"
                          aria-label={`Select ${lead.name}`}
                          checked={selectedIds.includes(lead.id)}
                          onChange={(event) => toggleOne(lead.id, event.target.checked)}
                          className="h-3.5 w-3.5 accent-dark-blue"
                        />
                      </td>
                      <td className="py-3 pr-2.5">
                        <div className="flex items-center gap-2">
                          <LeadProspectAvatar
                            picture={lead.avatarUrl}
                            initials={initials(lead.name)}
                            initialsClassName={lead.avatarClass}
                          />
                          <span className="text-xs font-semibold text-dark-blue">
                            {lead.name}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 pr-2.5">
                        {lead.lists.length === 0 ? (
                          <span className="text-xs text-muted">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {lead.lists.slice(0, 2).map((listName) => (
                              <em
                                key={`${lead.id}-${listName}`}
                                className="rounded-[4px] bg-primary px-1.5 py-0.5 text-[10px] not-italic text-dark-blue"
                              >
                                {listName}
                              </em>
                            ))}
                            {lead.lists.length > 2 ? (
                              <em className="rounded-[4px] bg-bg-grey px-1.5 py-0.5 text-[10px] not-italic text-muted">
                                +{lead.lists.length - 2}
                              </em>
                            ) : null}
                          </div>
                        )}
                      </td>
                      <td className="max-w-[220px] truncate py-3 pr-2.5 text-xs text-dark-blue">
                        {lead.company}
                      </td>
                      <td className="py-3 pr-2.5 text-xs text-muted">{lead.contact}</td>
                      <td className="py-3 pr-2.5">
                        <span
                          className={`inline-block rounded-xl px-1.5 py-1 text-[10px] font-semibold ${stageClass(
                            lead.stage
                          )}`}
                        >
                          {lead.stage}
                        </span>
                      </td>
                      <td className="py-3 pr-2.5 text-xs text-dark-blue">{lead.owner}</td>
                      <td className="py-3 text-xs text-muted">{lead.lastActivity}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-3">
            <ServerTablePagination
              page={Math.max(0, page - 1)}
              pageSize={pageSize}
              total={totalProspects}
              rowsPerPageOptions={[10, 25, 50]}
              loading={isLoading}
              onPageChange={(nextPage) => setPage(nextPage + 1)}
              onPageSizeChange={(nextSize) => {
                setPageSize(nextSize);
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <AddProspectsChooserModal
        open={showAddProspectsChooser}
        onClose={() => setShowAddProspectsChooser(false)}
        onFindProspects={() => {
          setShowAddProspectsChooser(false);
          navigate("/prospects/find");
        }}
        onImportProspects={() => {
          setShowAddProspectsChooser(false);
          void openImportListPicker();
        }}
      />

      <SelectListForImportModal
        isOpen={showImportListModal}
        onClose={() => setShowImportListModal(false)}
        lists={assignLists}
        onSelect={handleSelectListForImport}
        title="Select a list to import into"
        continueLabel="Continue to Import"
      />

      <SelectListForImportModal
        isOpen={showAssignModal}
        onClose={() => {
          if (isAssigning) return;
          setShowAssignModal(false);
        }}
        lists={assignLists}
        onSelect={(listId) => void handleAssignToList(listId)}
        title="Assign selected prospects to a list"
        continueLabel={isAssigning ? "Assigning…" : "Assign to List"}
        isSubmitting={isAssigning}
      />

      <CreateListModal
        isOpen={showCreateModal}
        onClose={() => {
          if (isCreating) return;
          setShowCreateModal(false);
          setNewListName("");
          setNewListDescription("");
        }}
        onSave={() => void handleCreateList()}
        listName={newListName}
        onListNameChange={setNewListName}
        description={newListDescription}
        onDescriptionChange={setNewListDescription}
        title={
          createWithSelection && selectedIds.length > 0
            ? `Create list from ${selectedIds.length} prospect${selectedIds.length === 1 ? "" : "s"}`
            : "Create List"
        }
        saveLabel={
          isCreating
            ? "Creating…"
            : createWithSelection && selectedIds.length > 0
            ? "Create & Add Prospects"
            : "Save"
        }
        isSubmitting={isCreating}
      />
    </section>
  );
};

export default LeadsDatabase;
